import type { Track } from "../schemas"

/** Music starts dipping this long before a word, so the first syllable isn't masked. */
export const DUCK_ATTACK_MS = 150
/** Music takes this long to come back up after speech ends. */
export const DUCK_RELEASE_MS = 400
/**
 * Words closer than attack + release are one speech range; this keeps the music down between
 * words instead of pumping, and keeps the per-range envelopes from overlapping.
 */
export const SPEECH_MERGE_GAP_MS = DUCK_ATTACK_MS + DUCK_RELEASE_MS

export type SpeechRange = [startMs: number, endMs: number]

/** Merges word timings (ms) into sorted, non-overlapping speech ranges. */
export function mergeSpeechRanges(words: SpeechRange[]): SpeechRange[] {
  const sorted = words.filter(([s, e]) => e > s).sort((a, b) => a[0] - b[0])
  const merged: SpeechRange[] = []
  for (const [s, e] of sorted) {
    const last = merged[merged.length - 1]
    if (last && s - last[1] <= SPEECH_MERGE_GAP_MS) last[1] = Math.max(last[1], e)
    else merged.push([s, e])
  }
  return merged
}

/**
 * Speech ranges from the caption words (deleted words excluded) united with the scene VAD speech
 * segments: transcripts drop or mistime words, and the music must still duck under real speech.
 */
export function speechRangesFromScript(
  tracks: Track[],
  speechSegments?: Array<{ start: number; end: number }>,
): SpeechRange[] {
  const words: SpeechRange[] = tracks
    .filter((t) => t.trackType === "CaptionTrack")
    .flatMap((t) => t.sections.flatMap((s) => s.segments))
    .flatMap((seg: any) => seg?.content?.textElements ?? [])
    .filter((el: any) => !el?.deleted && el?.timeline)
    .map((el: any) => [el.timeline.in, el.timeline.out] as SpeechRange)
  const segments = (speechSegments ?? []).map((s) => [s.start * 1000, s.end * 1000] as SpeechRange)
  return mergeSpeechRanges([...words, ...segments])
}

/** How far the music is ducked at `ms`: 0 = full volume, 1 = fully ducked. Linear attack/release ramps. */
export function duckAmountAt(ranges: SpeechRange[], ms: number): number {
  for (const [s, e] of ranges) {
    if (ms < s - DUCK_ATTACK_MS) return 0
    if (ms <= e + DUCK_RELEASE_MS) {
      const up = Math.min(1, (ms - (s - DUCK_ATTACK_MS)) / DUCK_ATTACK_MS)
      const down = Math.min(1, (e + DUCK_RELEASE_MS - ms) / DUCK_RELEASE_MS)
      return Math.max(0, Math.min(up, down))
    }
  }
  return 0
}

/** Music gain multiplier at `ms`; `duckLevelPct` is the music level (0-100) while someone speaks. */
export function duckGainAt(ranges: SpeechRange[], ms: number, duckLevelPct: number): number {
  const level = Math.max(0, Math.min(1, duckLevelPct / 100))
  return 1 - (1 - level) * duckAmountAt(ranges, ms)
}
